import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff, Check, XCircle, BadgeCheck, ArrowUp, ArrowDown, MessageSquare } from "lucide-react";
import { api, formatApiError, resolveImageUrl } from "../../lib/api";

const STATUS_LABEL = { pendente: "Pendente", aprovada: "Aprovada", rejeitada: "Rejeitada", oculta: "Oculta" };
const STATUS_STYLE = {
  pendente: "bg-yellow-50 text-yellow-700 border-yellow-200",
  aprovada: "bg-green-50 text-green-700 border-green-200",
  rejeitada: "bg-red-50 text-red-600 border-red-200",
  oculta: "bg-[#F2EDE4] text-[#6B645C] border-[#D5CDC2]",
};

function Stars({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= value ? "fill-[var(--brand)] text-[var(--brand)]" : "text-[#D5CDC2]"}`} />
      ))}
    </div>
  );
}

export default function AdminReviews({ settings, onToggleEnabled }) {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pendente");
  const [replyDrafts, setReplyDrafts] = useState({});

  const load = () => api.get(`/admin/reviews${filter ? `?status=${filter}` : ""}`).then((res) => setReviews(res.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const patch = async (id, data, msg) => {
    try {
      await api.patch(`/admin/reviews/${id}`, data);
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta avaliação definitivamente?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Avaliação excluída.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const move = (r, dir) => patch(r.id, { order_index: (r.order_index || 0) + dir });

  return (
    <div data-testid="admin-reviews">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display font-bold text-2xl tracking-tight">Avaliações de Clientes ({reviews.length})</h1>
        <button
          type="button"
          onClick={onToggleEnabled}
          data-testid="reviews-enabled-toggle"
          className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded-full border transition-colors ${
            settings.reviews_enabled ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"
          }`}
        >
          {settings.reviews_enabled ? "Recebendo avaliações" : "Avaliações desativadas"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-filters">
        {["pendente", "aprovada", "oculta", "rejeitada", ""].map((s) => (
          <button key={s || "todas"} type="button" onClick={() => setFilter(s)} data-testid={`review-filter-${s || "todas"}`}
            className={`px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider border transition-colors ${
              filter === s ? "bg-[#1A1816] text-white border-[#1A1816]" : "border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"
            }`}>
            {s ? STATUS_LABEL[s] : "Todas"}
          </button>
        ))}
      </div>

      {reviews.length === 0 ? (
        <p className="text-sm text-[#6B645C]" data-testid="reviews-empty">Nenhuma avaliação {filter ? STATUS_LABEL[filter].toLowerCase() : ""} no momento.</p>
      ) : (
        <div className="space-y-4" data-testid="admin-reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-3">
                  <Stars value={r.rating} />
                  <span className="font-display font-semibold text-sm">{r.customer_name}</span>
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                      <BadgeCheck className="w-3 h-3" /> verificada
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[11px] uppercase px-3 py-1 rounded-full border ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                  <span className="font-mono text-[11px] text-[#6B645C]">{new Date(r.created_at).toLocaleDateString("pt-BR")}</span>
                </div>
              </div>

              <p className="text-sm text-[#1A1816] mb-3">“{r.comment}”</p>
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mb-3">
                  {r.photos.map((p, i) => (
                    <img key={i} src={resolveImageUrl(p)} alt="Foto do cliente" className="w-14 h-14 rounded-xl object-cover border border-[#E8E2D9]" />
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {r.status !== "aprovada" && (
                  <button type="button" onClick={() => patch(r.id, { status: "aprovada" }, "Avaliação aprovada e publicada!")} data-testid={`review-approve-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-full hover:bg-green-100 transition-colors">
                    <Check className="w-3.5 h-3.5" /> Aprovar
                  </button>
                )}
                {r.status !== "rejeitada" && (
                  <button type="button" onClick={() => patch(r.id, { status: "rejeitada" }, "Avaliação rejeitada.")} data-testid={`review-reject-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-full hover:bg-red-100 transition-colors">
                    <XCircle className="w-3.5 h-3.5" /> Rejeitar
                  </button>
                )}
                <button type="button" onClick={() => patch(r.id, { status: r.status === "oculta" ? "aprovada" : "oculta" }, r.status === "oculta" ? "Avaliação exibida." : "Avaliação ocultada.")} data-testid={`review-hide-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs border border-[#D5CDC2] text-[#5A534C] px-3 py-1.5 rounded-full hover:border-[#1A1816] transition-colors">
                  {r.status === "oculta" ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {r.status === "oculta" ? "Exibir" : "Ocultar"}
                </button>
                <button type="button" onClick={() => patch(r.id, { featured: !r.featured }, r.featured ? "Destaque removido." : "Avaliação em destaque!")} data-testid={`review-feature-${r.id}`}
                  className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-colors ${r.featured ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"}`}>
                  <Star className="w-3.5 h-3.5" /> {r.featured ? "Destacada" : "Destacar"}
                </button>
                <button type="button" onClick={() => move(r, -1)} data-testid={`review-up-${r.id}`} className="p-1.5 rounded-full border border-[#D5CDC2] hover:border-[#1A1816] transition-colors" aria-label="Subir na ordem">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => move(r, 1)} data-testid={`review-down-${r.id}`} className="p-1.5 rounded-full border border-[#D5CDC2] hover:border-[#1A1816] transition-colors" aria-label="Descer na ordem">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-red-600 px-3 py-1.5 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                </button>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={replyDrafts[r.id] ?? r.reply ?? ""}
                  onChange={(e) => setReplyDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                  placeholder="Responder publicamente como Dra.Cell..."
                  data-testid={`review-reply-input-${r.id}`}
                  className="flex-1 bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
                />
                <button type="button" onClick={() => patch(r.id, { reply: replyDrafts[r.id] ?? r.reply }, "Resposta publicada!")} data-testid={`review-reply-save-${r.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#1A1816] text-white px-4 py-2 rounded-full font-display text-xs font-semibold hover:bg-[var(--brand)] transition-colors">
                  <MessageSquare className="w-3.5 h-3.5" /> Responder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
